import React from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { MuiButton, MuiChip, MuiBadge } from '@ace/ace-ui-web';
import { Launch } from '@mui/icons-material';
import { DynamicBaseUrlType, DynamicUrlField } from '../../constants/urlPaths';
import lang from '../../languages/en.json';
import styles from './MuiDataGrid.module.css';
import pagesStandardStyles from '../../pages/Pages.module.css';
import { kpiContentMap } from '../../utils/viewInfoUtils';
import * as constants from '../../constants/constants';
import type { ClickHandler } from '../../utils/usageMetricsLogger';
import { DrawerClickHandlerContext } from '../../utils/drawerClickContext';
import { OverflowTooltipValue } from '../../utils/dataGridRender';

import {
  formatLoadDate,
  formatKpiAmount,
  resolveDynamicUrl,
  roundToOneDecimal,
  isMobileDevice,
  isInternalEnterpriseUrl,
} from '../../utils/appUtils';

/**
 * The shape of values passed to renderValueOrRisk and resolveDecimalsAndAmount.
 * Wide because the API/data layer can return any of these for KPI amounts.
 */
export type KpiAmount = number | null | undefined | string;

/** Minimal row shape needed to format (and measure the width of) a KPI value cell. */
export interface KpiValueRow {
  kpiCode?: string;
  kpiFormat?: string;
  symbol?: string | null;
  currentKpiAmount?: KpiAmount;
}

/**
 * Context type for grid renderers - contains drawer state setters and dynamic URL builder
 */
export interface GridRenderContext {
  setDrawerContent: (content: {
    title: string;
    content: React.ReactNode;
    type: 'action' | 'info';
    width?: number;
  }) => void;
  setIsDrawerOpen: (open: boolean) => void;
  buildDynamicUrl?: (
    baseUrlType: DynamicBaseUrlType,
    kpiCode: string,
    field: DynamicUrlField,
  ) => string | null;
  isMultiStoreEnabled?: boolean;
  /** Optional click handler for usage metrics logging */
  handleAndLogClick?: ClickHandler;
  /** Optional handler to open the disclaimer dialog (e.g., for MPTC KPI link) */
  onDisclaimerOpen?: () => void;
  /** When true, suppresses hyperlinks in KPI name cells (print/PDF rendering) */
  isPrintView?: boolean;
}

/**
 * Shared helper function to render centered content
 */
export const renderCenteredContent = (content: React.ReactNode) => (
  <div className={styles.centeredContent}>{content}</div>
);

/**
 * Shared helper function to render left-aligned content
 */
export const renderLeftContent = (content: React.ReactNode) => (
  <div className={styles.leftContent}>{content}</div>
);

/**
 * Helper function to check if a KPI description is a sub-item
 */
const isSubItem = (description: string): boolean => {
  return description.startsWith('*');
};

/**
 * Returns true when the cell is an SO01 column in multi-store mode that should
 * render as a percentage instead of a YES/NO flag (currentKpi, priorKpi, or
 * benchmark). Used by the FLG bypass and the PCT override in renderValueOrRisk.
 */
const isSO01MultiStorePercentField = (
  params: GridRenderCellParams,
  context?: GridRenderContext,
): boolean =>
  params.row.kpiCode === 'SO01' &&
  Boolean(context?.isMultiStoreEnabled) &&
  (params.field === 'currentKpi' ||
    params.field === 'priorKpi' ||
    params.field === 'benchmark');

/**
 * Calculates decimal places and (optionally) rounds the amount for the value formatter,
 * based on KPI code, field, and format. For benchmark cells, uses currentKpiAmount as
 * the precision reference so it matches the Current/Prior columns.
 */
const resolveDecimalsAndAmount = (
  row: KpiValueRow,
  kpiField: string,
  kpiAmount: KpiAmount,
): { decimalPlaces: number; amount: KpiAmount } => {
  const kpiCode = row.kpiCode;
  const hasDecimals = Number(kpiAmount) % 1 !== 0;

  if (kpiCode === 'EE') {
    return { decimalPlaces: 2, amount: kpiAmount };
  }
  if (kpiField === 'kpiGoal') {
    return {
      decimalPlaces: hasDecimals ? 1 : 0,
      amount: hasDecimals ? roundToOneDecimal(kpiAmount) : kpiAmount,
    };
  }
  if (row.kpiFormat === constants.KPI_FORMAT.PERCENTAGE) {
    return { decimalPlaces: 1, amount: roundToOneDecimal(kpiAmount) };
  }
  if (row.kpiFormat === constants.KPI_FORMAT.DOLLAR) {
    // For benchmark field, use currentKpiAmount as the reference for decimal precision
    // so it matches the rounding of the Current and Prior columns.
    // If the reference has cents, show 2 decimal places; otherwise show whole dollars.
    const dollarReferenceAmount = kpiField === 'benchmark' ? row.currentKpiAmount : kpiAmount;
    const dollarReferenceHasDecimals = Number(dollarReferenceAmount) % 1 !== 0;
    return { decimalPlaces: dollarReferenceHasDecimals ? 2 : 0, amount: kpiAmount };
  }
  // Default: plain number format
  // For benchmark field, use currentKpiAmount as the reference for decimal precision
  // so it matches the rounding of the Current and Prior columns
  // If the reference has one decimal value, show 1 decimal place; otherwise show the value itself.
  const referenceAmount = kpiField === 'benchmark' ? row.currentKpiAmount : kpiAmount;
  const referenceHasDecimals = Number(referenceAmount) % 1 !== 0;
  return {
    decimalPlaces: referenceHasDecimals ? 1 : 0,
    amount: referenceHasDecimals ? roundToOneDecimal(kpiAmount) : kpiAmount,
  };
};

/**
 * Returns the plain text a numeric value cell renders, used only to measure column widths.
 * Mirrors the numeric/percent/dollar formatting in renderValueOrRisk (shared
 * resolveDecimalsAndAmount + formatKpiAmount). Short special-case outputs (dash, YES/NO,
 * N/M, risk chips) are never the widest value, so empty/raw text is returned for them —
 * width measurement only needs the widest string per column.
 */
export const getKpiValueText = (row: KpiValueRow, field: string, kpiAmount: KpiAmount): string => {
  if (kpiAmount === null || kpiAmount === undefined || kpiAmount === '') return '';
  if (Number.isNaN(Number(kpiAmount))) return String(kpiAmount);
  const symbol = field === 'kpiGoal' ? (row.symbol ?? null) : null;
  const { decimalPlaces, amount } = resolveDecimalsAndAmount(row, field, kpiAmount);
  return formatKpiAmount(amount, row.kpiFormat ?? '', symbol, decimalPlaces);
};

/**
 * Returns true when the cell should render a dash because it has no data,
 * or because the KPI has no goal value for the kpiGoal column.
 */
const shouldRenderDash = (
  params: GridRenderCellParams,
  kpiAmount: KpiAmount,
  context?: GridRenderContext,
): boolean => {
  if (kpiAmount === null || kpiAmount === undefined || kpiAmount === '') {
    return true;
  }
  const kpiCode = params.row.kpiCode;
  const kpiField = params.field;
  if (constants.NO_GOAL_KPI_CODES.includes(kpiCode) && kpiField === 'kpiGoal') {
    return true;
  }
  if (
    constants.NO_GOAL_KPI_CODES_MS.includes(kpiCode) &&
    context?.isMultiStoreEnabled &&
    kpiField === 'kpiGoal'
  ) {
    return true;
  }
  return false;
};

/**
 * Renders a colored Risk chip for Low/Medium/High values, or left-aligned
 * text for any other special string value (e.g., TXT-source KPIs).
 */
const renderRiskChip = (params: GridRenderCellParams): JSX.Element => {
  switch (params.value) {
    case lang.dataGrid.risk.low:
      return renderCenteredContent(
        <MuiChip
          label={lang.dataGrid.risk.low}
          size="small"
          colors="green"
        />,
      );
    case lang.dataGrid.risk.medium:
      return renderCenteredContent(
        <MuiChip
          label={lang.dataGrid.risk.medium}
          size="small"
          colors="yellow"
        />,
      );
    case lang.dataGrid.risk.high:
      return renderCenteredContent(
        <MuiChip
          label={lang.dataGrid.risk.high}
          size="small"
          colors="red"
        />,
      );
    default:
      return renderLeftContent(params.value);
  }
};

/**
 * Shared helper function to render Risk chip or formatted value
 */
export const renderValueOrRisk = (
  params: GridRenderCellParams,
  kpiAmount: KpiAmount,
  context?: GridRenderContext,
) => {
  // Benchmark column renders a dash for null/undefined/empty in any mode.
  // Without this, FLG KPIs would render null as 'NO' since Number(null) === 0.
  if (
    params.field === 'benchmark' &&
    (params.row.benchmark === null ||
      params.row.benchmark === undefined ||
      params.row.benchmark === '')
  ) {
    return renderCenteredContent('-');
  }

  if (
    constants.NO_METRIC_KPI_CODES.includes(params.row.kpiCode) &&
    (params.field === 'priorKpi' || params.field === 'currentKpi') &&
    (kpiAmount === undefined || Number(kpiAmount) === 0)
  ) {
    return renderLeftContent('N/M');
  }

  if (
    params.value &&
    typeof params.value === 'string' &&
    (constants.KPI_SPECIAL_VALUES.some(val => params.value.includes(val)) ||
      params.row.kpiSource === 'TXT')
  ) {
    return renderRiskChip(params);
  }

  if (
    params.row.kpiFormat === 'FLG' &&
    !isSO01MultiStorePercentField(params, context)
  ) {
    const kpiFlag = Number(kpiAmount) === 1 ? 'YES' : 'NO';
    return renderLeftContent(kpiFlag);
  }

  if (shouldRenderDash(params, kpiAmount, context)) {
    return renderCenteredContent('-');
  }

  const kpiField = params.field;
  const symbol = kpiField === 'kpiGoal' ? params.row.symbol : null;

  // SO01 in multi-store mode: treat currentKpi/priorKpi/benchmark as percentage
  if (isSO01MultiStorePercentField(params, context)) {
    params = { ...params, row: { ...params.row, kpiFormat: constants.KPI_FORMAT.PERCENTAGE } };
  }

  const { decimalPlaces, amount } = resolveDecimalsAndAmount(params.row, params.field, kpiAmount);
  const formattedAmount = formatKpiAmount(amount, params.row.kpiFormat, symbol, decimalPlaces);
  return <OverflowTooltipValue value={formattedAmount} />;
};

/**
 * Factory function to create KPI cell renderers with drawer context
 * This prevents re-creating functions on every render
 */
export const createKpiRenderers = (context: GridRenderContext) => {
  const { setDrawerContent, setIsDrawerOpen } = context;
  const isPrintView = context.isPrintView ?? false;

  return {
    /**
     * Renders KPI name cell with optional hyperlink, Mango Report chip, and Insight Program badge
     */
    renderKpiNameCell: (params: GridRenderCellParams) => {
      if (!params.row.description || params.row.description.trim() === '') {
        return renderCenteredContent('-');
      }

      const kpiCode = params.row.kpiCode;
      const contentInfo = kpiContentMap[kpiCode];
      const rawKpiHyperlink = contentInfo?.kpiHyperlink;

      // Resolve dynamic URL if prefix is present (e.g., "dynamicURL:WFC:current")
      const resolvedHyperlink = resolveDynamicUrl(
        rawKpiHyperlink,
        kpiCode,
        context.buildDynamicUrl,
      );

      // Split description at parenthesis only if text ends with (...)
      const description = params.row.description;
      const match = description.match(/^(.+?)\s*(\([^)]+\))$/);
      // Replace leading '*' with '•' for display
      const mainText = (match ? match[1].trim() : description).replace(/^\*/, '•');
      const subText = match ? match[2].trim() : null;

      // Suppress hyperlinks for internal enterprise URLs on mobile to avoid redirect issues
      const shouldShowLink =
        resolvedHyperlink !== null &&
        !(isMobileDevice() && isInternalEnterpriseUrl(resolvedHyperlink));

      return (
        <div className={styles.kpiCellContainer}>
          <div className={styles.kpiRowContent}>
            {shouldShowLink && !isPrintView ? (
              <a
                href={resolvedHyperlink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={e => {
                  e.preventDefault();
                  if (
                    contentInfo?.isDisclaimerRequired &&
                    context.onDisclaimerOpen &&
                    context.handleAndLogClick
                  ) {
                    context.handleAndLogClick(`KPI Link - ${mainText}`, {
                      callback: context.onDisclaimerOpen,
                    });
                  } else if (context.handleAndLogClick) {
                    context.handleAndLogClick(`KPI Link - ${mainText}`, { url: resolvedHyperlink });
                  } else if (resolvedHyperlink) {
                    if (isInternalEnterpriseUrl(resolvedHyperlink)) {
                      window.open(resolvedHyperlink, '_blank');
                    } else {
                      window.open(resolvedHyperlink, '_blank', 'noopener,noreferrer');
                    }
                  }
                }}
                className={
                  isSubItem(description)
                    ? `${styles.kpiSubItem} ${pagesStandardStyles.viewInfoLink}`
                    : `${styles.kpiMainItem} ${pagesStandardStyles.viewInfoLink}`
                }>
                {mainText}
                <Launch
                  fontSize="small"
                  className={styles.launchIcon}
                />
              </a>
            ) : (
              <div className={isSubItem(description) ? styles.kpiSubItem : styles.kpiMainItem}>
                {mainText}
              </div>
            )}
            {constants.MANGO_REPORT_KPI.includes(kpiCode) && (
              <MuiChip
                label={lang.dataGrid.actions.mangoReport}
                size="small"
                colors="green"
              />
            )}
            {constants.INSIGHT_PROGRAM_KPI_CODES.includes(kpiCode) && (
              <img
                src={`${process.env.PUBLIC_URL}/images/StoreOps-Insight.png`}
                alt="Insight Program"
                className={styles.insightProgramImage}
              />
            )}
          </div>
          {subText && <div className={styles.kpiSubText}>{subText}</div>}
          {kpiCode === 'CRPC' && params.row.loadDate && (
            <div className={styles.kpiSubText}>• as of {formatLoadDate(params.row.loadDate)}</div>
          )}
        </div>
      );
    },

    /**
     * Renders "View Info" link that opens drawer with KPI information
     */
    renderMoreInfoCell: (params: GridRenderCellParams) => {
      const kpiCode = params.row.kpiCode;

      if (constants.NO_INFO_KPI_CODES.includes(kpiCode)) {
        return renderCenteredContent('-');
      }

      const contentInfo = kpiContentMap[kpiCode];
      if (!contentInfo) {
        return renderCenteredContent('-');
      }

      return (
        <a
          href="#"
          onClick={e => {
            e.preventDefault();
            const ContentComponent = contentInfo.component;
            const openDrawer = () => {
              setDrawerContent({
                title: contentInfo.title || lang.pageContent.moreInformation,
                content: <ContentComponent />,
                type: 'info',
              });
              setIsDrawerOpen(true);
            };
            if (context.handleAndLogClick) {
              context.handleAndLogClick(
                `View Info - ${params.row.description || contentInfo.title || kpiCode}`,
                {
                  callback: openDrawer,
                },
              );
            } else {
              openDrawer();
            }
          }}
          className={pagesStandardStyles.viewInfoLink}>
          {lang.pageContent.viewInfo}
        </a>
      );
    },

    /**
     * Renders "Take Action" button that opens drawer or navigates to external link
     */
    renderTakeActionCell: (params: GridRenderCellParams) => {
      // Handle zero current value or multi-store restricted KPIs by showing a dash instead of action button
      const isZeroCurrentValue =
        constants.NO_ZERO_CURRENT_VALUE_KPI_CODES.includes(params.row.kpiCode) &&
        (params.row.currentKpiAmount === 0 ||
          params.row.currentKpiAmount === '0' ||
          params.row.currentKpiAmount === null);
      const isMultiStoreRestricted =
        context.isMultiStoreEnabled &&
        constants.NO_MULTISTORE_MODE_KPI_CODES.includes(params.row.kpiCode);
      if (isZeroCurrentValue || isMultiStoreRestricted) {
        return renderCenteredContent('-');
      }
      const kpiCode = params.row.kpiCode;
      const contentInfo = kpiContentMap[kpiCode];

      const hasActionContent = contentInfo?.actionContent;
      // Resolve dynamic URL - use row-level URL for focus areas, otherwise use contentInfo
      const actionUrl = resolveDynamicUrl(
        params.row.isFocusArea ? params.row.actionHyperlink : contentInfo?.actionHyperlink,
        kpiCode,
        context.buildDynamicUrl,
      );

      if (!hasActionContent && !actionUrl) {
        return renderCenteredContent('-');
      }

      const actionLabel: string = params.row.isFocusArea
        ? (params.row.takeActionTitle ?? kpiCode)
        : (contentInfo?.actionTitle ?? kpiCode);

      // Build a unique click identifier by appending the KPI title from the row description
      // This distinguishes rows that share the same action button label (e.g., two "Review Medallia Dashboard" buttons)
      const rawDescription: string = params.row.description ?? '';
      const lastOpenParen: number = rawDescription.lastIndexOf('(');
      const kpiTitle: string = (
        lastOpenParen > 0 ? rawDescription.slice(0, lastOpenParen).trim() : rawDescription
      ).replace(/^\*/, '•');
      const clickId: string = kpiTitle
        ? `Take Action - ${actionLabel} (${kpiTitle})`
        : `Take Action - ${actionLabel}`;

      return (
        <MuiButton
          colors="secondary"
          size="small"
          iconType="launch"
          iconPosition="right"
          onClick={() => {
            if (contentInfo?.actionContent) {
              const ActionContent = contentInfo.actionContent;
              const openDrawer = () => {
                setDrawerContent({
                  title: contentInfo?.actionTitle ?? '',
                  content: (
                    <DrawerClickHandlerContext.Provider value={context.handleAndLogClick}>
                      <ActionContent />
                    </DrawerClickHandlerContext.Provider>
                  ),
                  type: 'action',
                  width: contentInfo?.actionDrawerWidth,
                });
                setIsDrawerOpen(true);
              };
              if (context.handleAndLogClick) {
                context.handleAndLogClick(clickId, { callback: openDrawer });
              } else {
                openDrawer();
              }
            } else if (actionUrl) {
              if (context.handleAndLogClick) {
                context.handleAndLogClick(clickId, { url: actionUrl });
              } else {
                window.open(actionUrl, '_blank');
              }
            }
          }}>
          {actionLabel}
        </MuiButton>
      );
    },

    /**
     * Renders KPI progress indicator badge (success/error)
     */
    renderKpiProgressIcon: (params: GridRenderCellParams) => {
      if (params.row.goalFlag === constants.GOAL_FLAG.MET) {
        return (
          <div className={styles.badgeContainer}>
            <MuiBadge
              color="success"
              iconType="check"
              variant="standard"
            />
          </div>
        );
      }
      if (params.row.goalFlag === constants.GOAL_FLAG.NOT_MET) {
        return (
          <div className={styles.badgeContainer}>
            <MuiBadge
              color="error"
              iconType="priority"
              variant="standard"
            />
          </div>
        );
      }
      if (params.row.goalFlag === constants.GOAL_FLAG.AT_RISK) {
        return (
          <div className={styles.badgeContainer}>
            <MuiBadge
              color="warning"
              variant="standard"
              iconType="warning"
            />
          </div>
        );
      }
      return renderCenteredContent('-');
    },
  };
};
